import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, Save, SkipForward, Store, LayoutDashboard } from "lucide-react";
import { useTranslation } from '../../../contexts/LanguageContext';
import { useAuth } from "../../../App"; // Import the auth hook

const MobileSubscriptionCollector = ({ merchants, onComplete, refreshDashboardData }) => {
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  const navigate = useNavigate();
  
  const [currentMerchantIndex, setCurrentMerchantIndex] = useState(0);
  const [isActiveCustomer, setIsActiveCustomer] = useState(null);
  const [inFreeTrial, setInFreeTrial] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [billDate, setBillDate] = useState("");
  const [trialStartDate, setTrialStartDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [animationClass, setAnimationClass] = useState("fade-in");
  const [showCustomPlanInput, setShowCustomPlanInput] = useState(false);
  const [customPlan, setCustomPlan] = useState("");
  const [customPlanPrice, setCustomPlanPrice] = useState("");
  const [customPlanInterval, setCustomPlanInterval] = useState("monthly");
  const [skipBillingDate, setSkipBillingDate] = useState(false);
  const [skippedMerchants, setSkippedMerchants] = useState(new Set());
  const [logoError, setLogoError] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const formContainerRef = useRef(null);

  // Validate merchants prop
  useEffect(() => {
    if (!merchants || !Array.isArray(merchants) || merchants.length === 0) {
      console.error("MobileSubscriptionCollector: Invalid merchants prop", merchants);
      if (onComplete) {
        onComplete();
      }
      return;
    }
    
    // Check if all merchants have required properties
    const invalidMerchants = merchants.filter(m => 
      !m || !m.id || !m.name
    );
    
    if (invalidMerchants.length > 0) {
      console.error("MobileSubscriptionCollector: Some merchants missing required properties", invalidMerchants);
    }
  }, [merchants, onComplete]);

  // Get current merchant with null check
  const currentMerchant = merchants && merchants.length > 0 && currentMerchantIndex < merchants.length
    ? merchants[currentMerchantIndex]
    : null;

  // Reset logo error when merchant changes
  useEffect(() => {
    setLogoError(false);
  }, [currentMerchantIndex]);

  // Reset index if out of bounds
  useEffect(() => {
    if (merchants && merchants.length > 0 && currentMerchantIndex >= merchants.length) {
      setCurrentMerchantIndex(0);
    }
  }, [merchants, currentMerchantIndex]);

  useEffect(() => {
    if (currentMerchant) {
      setAnimationClass("fade-in");
      const timer = setTimeout(() => setAnimationClass(""), 500);
      return () => clearTimeout(timer);
    }
  }, [currentMerchantIndex, currentMerchant]);

  const goToNextMerchant = () => {
    setAnimationClass("fade-out");
    setTimeout(() => {
      if (currentMerchantIndex < merchants.length - 1) {
        setCurrentMerchantIndex(currentMerchantIndex + 1);
        resetForm();
      } else {
        handleComplete();
      }
    }, 300);
  };

  const goToPrevMerchant = () => {
    if (currentMerchantIndex > 0) {
      setAnimationClass("fade-out");
      setTimeout(() => {
        setCurrentMerchantIndex(currentMerchantIndex - 1);
        resetForm();
      }, 300);
    }
  };

  const handleNoClick = () => {
    setIsActiveCustomer(false);
    if (currentMerchant?.id) {
      setSkippedMerchants(prev => new Set(prev).add(currentMerchant.id));
    }
    goToNextMerchant();
  };

  const resetForm = () => {
    setIsActiveCustomer(null);
    setInFreeTrial(null);
    setSelectedPlan("");
    setBillDate("");
    setTrialStartDate("");
    setShowCustomPlanInput(false);
    setCustomPlan("");
    setCustomPlanPrice("");
    setCustomPlanInterval("monthly");
    setSkipBillingDate(false);
  };

  const hasPlanData = () =>
    selectedPlan || (showCustomPlanInput && customPlan && customPlanPrice);

  const canSubmitForm = () => {
    if (isActiveCustomer !== true) return false;
    
    if (!hasPlanData()) return false;
    
    if (showCustomPlanInput && (!customPlan || !customPlanPrice)) return false;
    
    if (inFreeTrial === null) return false;
    
    if (inFreeTrial === true) {
      if (!trialStartDate) return false;
      const trialEnd = new Date(trialStartDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (trialEnd <= today) return false;
    } else if (inFreeTrial === false) {
      if (!billDate && !skipBillingDate) return false;
    }
    
    return true;
  };

  const markInitialSetupCompleted = async () => {
    try {
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const response = await httpClient.request(
        "/api/mark-initial-setup-completed",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return markInitialSetupCompleted();
      }

      if (!response.ok) {
        console.error("Failed to mark initial setup as completed");
      }
      
      return await response.json();
    } catch (error) {
      console.error("Error marking initial setup:", error);
      throw error;
    }
  };

  const saveSubscriptionData = async () => {
    if (!canSubmitForm() || !currentMerchant) return;

    const merchantCategory = currentMerchant.category || "Other";
    
    // Check if it's a free trial
    if (inFreeTrial === true) {
      // Save as FREE TRIAL
      const freeTrialData = {
        merchant_id: currentMerchant.id,
        merchant_name: currentMerchant.name,
        plan_id: showCustomPlanInput ? `custom-${Date.now()}` : selectedPlan,
        plan_name: showCustomPlanInput
          ? customPlan
          : currentMerchant.plans?.find((p) => p.id === selectedPlan)?.name || "",
        price: showCustomPlanInput
          ? parseFloat(customPlanPrice)
          : currentMerchant.plans?.find((p) => p.id === selectedPlan)?.price || 0,
        currency: showCustomPlanInput
          ? "EUR"
          : currentMerchant.plans?.find((p) => p.id === selectedPlan)?.currency || "EUR",
        billing_interval: showCustomPlanInput
          ? customPlanInterval
          : currentMerchant.plans?.find((p) => p.id === selectedPlan)?.billing_interval || "monthly",
        category: merchantCategory,
        end_of_trial: new Date(trialStartDate).toISOString(),
        automatic_renewal: true,
        status: "active",
      };

      try {
        const response = await httpClient.request(
          "/api/add-free-trial",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              free_trials: [freeTrialData],
            }),
          }
        );

        // Handle token expiration
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return saveSubscriptionData();
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${await response.text()}`);
        }
        
        // Dispatch event for dashboard to refresh free trials
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('subscriptionsUpdated', { 
            detail: { type: 'freeTrial', data: freeTrialData }
          }));
        }
        
        return await response.json();
      } catch (error) {
        console.error("Error saving free trial:", error);
        throw error;
      }
    } else {
      // Save as REGULAR SUBSCRIPTION
      const subscriptionData = {
        merchant_id: currentMerchant.id,
        merchant_name: currentMerchant.name,
        plan_id: showCustomPlanInput ? `custom-${Date.now()}` : selectedPlan,
        plan_name: showCustomPlanInput
          ? customPlan
          : currentMerchant.plans?.find((p) => p.id === selectedPlan)?.name || "",
        price: showCustomPlanInput
          ? parseFloat(customPlanPrice)
          : currentMerchant.plans?.find((p) => p.id === selectedPlan)?.price || 0,
        currency: showCustomPlanInput
          ? "EUR"
          : currentMerchant.plans?.find((p) => p.id === selectedPlan)?.currency || "EUR",
        billing_interval: showCustomPlanInput
          ? customPlanInterval
          : currentMerchant.plans?.find((p) => p.id === selectedPlan)?.billing_interval || "monthly",
        category: merchantCategory,
        is_custom: showCustomPlanInput,
        start_date: new Date().toISOString(),
        status: "active",
        ...(!skipBillingDate && { next_billing_date: billDate }),
      };

      try {
        const response = await httpClient.request(
          "/api/save-subscriptions",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              subscriptions: [subscriptionData],
            }),
          }
        );

        // Handle token expiration
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return saveSubscriptionData();
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${await response.text()}`);
        }
        
        // Dispatch event for dashboard to refresh subscriptions
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('subscriptionsUpdated', { 
            detail: { type: 'subscription', data: subscriptionData }
          }));
        }
        
        return await response.json();
      } catch (error) {
        console.error("Error saving subscription:", error);
        throw error;
      }
    }
  };

  const handleSkipAll = async () => {
    // Check authentication
    if (!user) {
      navigate("/demo");
      return;
    }

    setIsLoading(true);
    try {
      await markInitialSetupCompleted();
    } catch (error) {
      console.error("Error skipping setup:", error);
    } finally {
      showCompletionScreen();
    }
  };

  const handleSaveAndSkip = async () => {
    // Check authentication
    if (!user) {
      navigate("/demo");
      return;
    }

    setIsLoading(true);
    try {
      if (canSubmitForm() && currentMerchant) {
        await saveSubscriptionData();
      }
      await markInitialSetupCompleted();
    } catch (error) {
      console.error("Error saving before skip:", error);
    } finally {
      showCompletionScreen();
    }
  };

  const handleNext = async () => {
    // Check authentication
    if (!user) {
      navigate("/demo");
      return;
    }

    setIsLoading(true);
    try {
      if (canSubmitForm() && currentMerchant) {
        await saveSubscriptionData();
      }
    } catch (error) {
      console.error("Error saving before next:", error);
    } finally {
      setIsLoading(false);
      goToNextMerchant();
    }
  };

  const handleComplete = async () => {
    // Check authentication
    if (!user) {
      navigate("/demo");
      return;
    }

    setIsLoading(true);
    try {
      await markInitialSetupCompleted();
    } catch (error) {
      console.error("Error completing setup:", error);
    } finally {
      showCompletionScreen();
    }
  };

  const showCompletionScreen = () => {
    setIsLoading(false);
    setShowCompletionMessage(true);
    setCompleted(true);
    // DO NOT call onComplete here - we'll call it when user chooses an option
  };

  const handleGoToMarketplace = () => {
    // Call onComplete before navigating to ensure parent knows we're done
    if (onComplete) onComplete();
    
    // Trigger dashboard refresh if callback provided
    if (refreshDashboardData) {
      refreshDashboardData();
    }
    
    // Dispatch global event for any other listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('subscriptionsUpdated'));
    }
    
    navigate("/mobile-marketplace");
  };

  const handleGoToDashboard = () => {
    // Call onComplete before navigating to ensure parent knows we're done
    if (onComplete) onComplete();
    
    // Trigger dashboard refresh if callback provided
    if (refreshDashboardData) {
      refreshDashboardData();
    }
    
    // Dispatch global event for any other listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('subscriptionsUpdated'));
    }
    
    navigate("/mobile-dashboard");
  };

  // If showing completion message, render it
  if (showCompletionMessage) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-auto transform transition-all duration-300 animate-in fade-in zoom-in-95">
          <div className="p-6 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-xl font-bold mb-2 text-gray-800">
              {t('subscriptionCollector.completionTitle', 'Great job!')}
            </h2>
            
            <p className="text-gray-600 mb-6">
              {t('subscriptionCollector.completionDescription', 'You can add more subscriptions from our Marketplace or go to your dashboard to see your current subscriptions.')}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleGoToMarketplace}
                className="w-full py-3 bg-[#253985] text-white rounded-lg font-medium hover:bg-[#1d2e6f] transition-colors duration-200 flex items-center justify-center"
              >
                <Store className="h-5 w-5 mr-2" />
                {t('subscriptionCollector.goToMarketplace', 'Go to Marketplace')}
              </button>
              
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 bg-gray-100 text-gray-800 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
              >
                <LayoutDashboard className="h-5 w-5 mr-2" />
                {t('subscriptionCollector.goToDashboard', 'Go to Dashboard')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If completed but not showing message (shouldn't happen), return null
  if (completed) return null;
  
  // If there's no data or invalid data, don't render
  if (!merchants || merchants.length === 0) {
    console.log("MobileSubscriptionCollector: Not rendering due to no merchants");
    return null;
  }

  // If currentMerchant is invalid, skip to next or complete
  if (!currentMerchant || !currentMerchant.id || !currentMerchant.name) {
    console.error("MobileSubscriptionCollector: Invalid current merchant at index", currentMerchantIndex, currentMerchant);
    
    // Try to skip to next merchant if possible
    if (currentMerchantIndex < merchants.length - 1) {
      setTimeout(() => {
        setCurrentMerchantIndex(prev => prev + 1);
      }, 100);
      return null;
    }
    
    // If no more merchants, complete
    if (onComplete) {
      setTimeout(() => {
        onComplete();
      }, 100);
    }
    return null;
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]">
        <div className="bg-white p-6 rounded-xl shadow-xl w-[90%] max-w-sm mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#253985] mx-auto mb-4"></div>
            <h2 className="text-xl font-bold mb-2 text-gray-800">
              {t('subscriptionCollector.processingData', 'Processing your data')}
            </h2>
            <p className="text-gray-600 text-sm">
              {t('subscriptionCollector.organizingSubscriptions', 'We\'re organizing your subscriptions...')}
            </p>
            <div className="mt-4 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
              <div className="animate-progress h-full bg-[#253985] rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div 
        className={`bg-white rounded-xl shadow-xl w-full max-w-md mx-auto transform transition-all duration-300 ${animationClass} flex flex-col`}
        style={{ maxHeight: "90vh" }}
      >
        {/* Header with progress and skip options */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button
            onClick={handleSkipAll}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center"
          >
            <X className="h-4 w-4 mr-1" />
            {t('subscriptionCollector.skipAll', 'Skip All')}
          </button>
          
          <div className="text-sm text-gray-500">
            {currentMerchantIndex + 1} {t('common.of', 'of')} {merchants.length}
          </div>
          
          <button
            onClick={handleSaveAndSkip}
            className="text-sm text-[#253985] hover:text-[#1d2e6f] font-medium flex items-center"
          >
            <SkipForward className="h-4 w-4 mr-1" />
            {t('subscriptionCollector.saveAndSkip', 'Save & Skip')}
          </button>
        </div>

        <div className="pt-4 px-5">
          {/* Safe logo rendering with fallback */}
          <div className="flex items-center justify-center mb-4">
            <div className="h-10 w-10 flex items-center justify-center bg-gray-100 rounded-lg">
              {currentMerchant.logo && !logoError ? (
                <img 
                  src={currentMerchant.logo} 
                  alt={currentMerchant.name} 
                  className="h-10 w-10 object-contain"
                  onError={() => setLogoError(true)}
                  onLoad={() => setLogoError(false)}
                />
              ) : (
                <div className="h-10 w-10 flex items-center justify-center bg-gray-200 rounded-lg">
                  <span className="text-gray-600 font-medium text-xs">
                    {currentMerchant.name.substring(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <h2 className="text-xl font-bold text-center mb-4 text-gray-800">
            {t('subscriptionCollector.activeSubscriptionQuestion', 'Do you have an active subscription with')}{" "}
            <span className="text-[#253985]">{currentMerchant.name}</span>?
          </h2>
          
          <div className="flex space-x-3 mb-5">
            <button
              onClick={() => setIsActiveCustomer(true)}
              className={`flex-1 py-3 rounded-lg transition-all duration-200 ${
                isActiveCustomer === true
                  ? "bg-[#253985] text-white shadow-md"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
              } font-medium text-sm`}
            >
              {t('common.yes', 'Yes')}
            </button>
            <button
              onClick={handleNoClick}
              className={`flex-1 py-3 rounded-lg transition-all duration-200 ${
                isActiveCustomer === false
                  ? "bg-[#253985] text-white shadow-md"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
              } font-medium text-sm`}
            >
              {t('common.no', 'No')}
            </button>
          </div>
        </div>
  
        {/* Scrollable content area */}
        <div 
          ref={formContainerRef}
          className="px-5 overflow-y-auto flex-1"
          style={{ maxHeight: "calc(90vh - 280px)" }}
        >
          {isActiveCustomer === true && currentMerchant && (
            <div className="mb-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('subscriptionCollector.selectYourPlan', 'Select your plan')}
                </label>
                <div className="space-y-2 mb-3">
                  {currentMerchant.plans?.map((plan) => (
                    <div 
                      key={plan.id}
                      onClick={() => {
                        setSelectedPlan(plan.id);
                        setShowCustomPlanInput(false);
                        setTimeout(() => {
                          formContainerRef.current?.scrollTo({
                            top: formContainerRef.current.scrollHeight * 0.4,
                            behavior: "smooth",
                          });
                        }, 50);
                      }}
                      className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                        selectedPlan === plan.id && !showCustomPlanInput
                          ? "border-[#253985] bg-[#253985]/10"
                          : "border-gray-200 hover:border-[#253985]"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">{plan.name}</span>
                        <span className="text-[#253985] font-semibold text-sm">
                          {plan.currency}{plan.price}/{plan.billing_interval === 'monthly' ? t('common.perMonthShort', 'mo') : t('common.perYearShort', 'yr')}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  <div 
                    onClick={() => {
                      setShowCustomPlanInput(true);
                      setSelectedPlan("");
                      setTimeout(() => {
                        formContainerRef.current?.scrollTo({
                          top: formContainerRef.current.scrollHeight,
                          behavior: 'smooth'
                        });
                      }, 50);
                    }}
                    className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                      showCustomPlanInput
                        ? "border-[#253985] bg-[#253985]/10"
                        : "border-gray-200 hover:border-[#253985]"
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">
                        {t('subscriptionCollector.planNotListed', 'My plan isn\'t listed')}
                      </span>
                      <span className="text-[#253985] font-semibold">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
  
                {/* Custom plan input form */}
                {showCustomPlanInput && (
                  <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('subscriptionCollector.planName', 'Plan Name')}
                      </label>
                      <input
                        type="text"
                        value={customPlan}
                        onChange={(e) => setCustomPlan(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
                        placeholder={t('subscriptionCollector.planNamePlaceholder', 'e.g. Premium Plus')}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t('common.price', 'Price')}
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">€</span>
                          </div>
                          <input
                            type="number"
                            value={customPlanPrice}
                            onChange={(e) => setCustomPlanPrice(e.target.value)}
                            className="w-full pl-7 p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t('subscriptionCollector.billing', 'Billing')}
                        </label>
                        <select
                          value={customPlanInterval}
                          onChange={(e) => setCustomPlanInterval(e.target.value)}
                          className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
                        >
                          <option value="monthly">{t('common.monthly', 'Monthly')}</option>
                          <option value="yearly">{t('common.yearly', 'Yearly')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
  
              {(selectedPlan || showCustomPlanInput) && (
                <>
                  <div>
                    <h3 className="block text-sm font-medium text-gray-700 mb-2">
                      {t('subscriptionCollector.freeTrialQuestion', 'Are you currently in a free trial?')}
                    </h3>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setInFreeTrial(true);
                          setTimeout(() => {
                            formContainerRef.current?.scrollTo({
                              top: formContainerRef.current.scrollHeight,
                              behavior: "smooth",
                            });
                          }, 50);
                        }}
                        className={`flex-1 py-2 rounded-lg transition-all duration-200 ${
                          inFreeTrial === true
                            ? "bg-[#253985] text-white shadow-md"
                            : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                        } text-xs font-medium`}
                      >
                        {t('common.yes', 'Yes')}
                      </button>
                      <button
                        onClick={() => {
                          setInFreeTrial(false);
                          setTimeout(() => {
                            formContainerRef.current?.scrollTo({
                              top: formContainerRef.current.scrollHeight,
                              behavior: "smooth",
                            });
                          }, 50);
                        }}
                        className={`flex-1 py-2 rounded-lg transition-all duration-200 ${
                          inFreeTrial === false
                            ? "bg-[#253985] text-white shadow-md"
                            : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                        } text-xs font-medium`}
                      >
                        {t('common.no', 'No')}
                      </button>
                    </div>
                  </div>
  
                  {inFreeTrial === true && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('subscriptionCollector.trialEndDate', 'When does your free trial end?')}
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          value={trialStartDate}
                          onChange={(e) => {
                            const selectedDate = new Date(e.target.value);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            
                            if (selectedDate <= today) {
                              alert(t('subscriptionCollector.dateMustBeFuture', 'Trial end date must be in the future'));
                              return;
                            }
                            setTrialStartDate(e.target.value);
                          }}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
                          required
                        />
                      </div>
                    </div>
                  )}
  
                  {inFreeTrial === false && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('subscriptionCollector.nextBillingDate', 'Next billing date')}
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          value={billDate}
                          onChange={(e) => setBillDate(e.target.value)}
                          className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
                          required={!skipBillingDate}
                          disabled={skipBillingDate}
                        />
                      </div>
                      <div className="mt-2 flex items-center">
                        <input
                          id="skip-billing-date"
                          type="checkbox"
                          checked={skipBillingDate}
                          onChange={(e) => setSkipBillingDate(e.target.checked)}
                          className="h-4 w-4 text-[#253985] focus:ring-[#253985] border-gray-300 rounded"
                        />
                        <label htmlFor="skip-billing-date" className="ml-2 block text-xs text-gray-700">
                          {t('subscriptionCollector.dontKnowBillingDate', 'I don\'t know my billing date')}
                        </label>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
  
        {/* Bottom navigation */}
        <div className="px-5 pb-5 pt-4 border-t border-gray-200 bg-white rounded-b-xl">
          <div className="flex justify-between items-center">
            <button
              onClick={goToPrevMerchant}
              disabled={currentMerchantIndex === 0}
              className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 text-sm flex items-center ${
                currentMerchantIndex === 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-[#253985] hover:text-[#1d2e6f]"
              }`}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {t('common.previous', 'Previous')}
            </button>
            
            <div className="flex items-center space-x-2">
              <div className="text-xs text-gray-500">
                {currentMerchantIndex + 1} {t('common.of', 'of')} {merchants.length}
              </div>
              <button
                onClick={handleNext}
                disabled={!canSubmitForm()}
                className={`px-4 py-2.5 rounded-lg font-medium transition-all duration-200 text-sm ${
                  !canSubmitForm()
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-[#253985] text-white hover:bg-[#1d2e6f] shadow-md"
                } flex items-center`}
              >
                {currentMerchantIndex < merchants.length - 1 
                  ? t('subscriptionCollector.saveAndNext', 'Save & Next') 
                  : t('subscriptionCollector.finish', 'Finish')}
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
          
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-[#253985] h-1.5 rounded-full transition-all duration-500" 
                style={{ width: `${((currentMerchantIndex + 1) / merchants.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileSubscriptionCollector;